// Small framework-agnostic store with a React binding via useSyncExternalStore.
// Holds the live-feed projection: a synthetic activity feed, recent calls, task
// index, queue depths, stream/channel index, per-client heartbeat metrics,
// client-status + publication index, and recent object-store changes. Views read
// slices; the event-bus transport pushes validated frames in (malformed frames
// are dropped upstream). Frames arrive in the FROZEN {type,ts,id,data} envelope;
// applyFrame ADAPTS the frozen snake_case payloads into the internal projection.

import { useSyncExternalStore } from 'react';
import type { EventFrame } from '../api/events';
import type { AuditEvent, CallLog, Channel, GpuStat, McpSession } from '../api/schemas';
import { SeriesStore } from '../lib/timeseries';

// ST43 — node object-store / disk occupancy from the heartbeat's `store` block.
export interface LiveStoreMetrics {
  bytes?: number;
  objects?: number;
  disk_free?: number;
  disk_total?: number;
}

export interface LiveMetrics {
  client_id: string;
  cpu_pct?: number;
  ram_used_mb?: number;
  ram_total_mb?: number;
  gpus: GpuStat[];
  credits?: number;
  queue_depth?: number;
  store?: LiveStoreMetrics;
  ts: number;
}

export interface LiveTask {
  id: string;
  canonical_tool: string;
  network_id?: string;
  state: string;
  priority: number;
  fraction?: number;
  provider_instance_id?: string;
  // ST42 — terminal outcome from task.completed (shown in an open drawer without a refetch).
  result?: unknown;
  output_refs?: string[];
  error?: string;
  ts: number;
}

export interface LiveQueue {
  canonical_tool: string;
  depth: number;
  oldest_wait_ms?: number;
  ts: number;
}

export interface LiveStream {
  id: string;
  canonical_tool?: string;
  channels: Channel[];
  consumer_count: number;
  state: 'ACTIVE' | 'CLOSED';
  ts: number;
}

export interface LiveClient {
  id: string;
  status: string;
  node_profile?: unknown;
  ts: number;
}

export interface LivePublication {
  id: string;
  network_id: string;
  service_slug?: string;
  status: string;
  ts: number;
}

export interface LiveObject {
  s3_key: string;
  network_id?: string;
  op: string;
  instance?: string;
  size?: number;
  is_global?: boolean;
  ts: number;
}

export interface LiveRouteInstance {
  instance_id: string;
  route_id: string;
  route_name: string;
  network_id: string;
  version: number;
  status: 'active' | 'completed' | 'errored';
  hops: string[];
  error?: string;
  ts: number;
}

export interface DashboardState {
  connected: boolean;
  feed: AuditEvent[]; // synthetic activity log (newest first, bounded)
  calls: CallLog[]; // newest first, bounded (adapted from call.logged)
  tasks: Record<string, LiveTask>; // by task_id
  queues: Record<string, LiveQueue>; // by canonical_tool
  streams: Record<string, LiveStream>; // by stream_id
  metrics: Record<string, LiveMetrics>; // by client_id
  clients: Record<string, LiveClient>; // latest status by client id
  publications: Record<string, LivePublication>; // latest change by pub id
  objects: Record<string, LiveObject>; // latest change by s3_key
  routeInstances: Record<string, LiveRouteInstance>; // by instance_id (ADR-020 R7)
  sessions: Record<string, LiveSession>; // /mcp sessions by session_id — active + recently-closed (H64/H67)
  // ST39 — in-memory downsampled time-series (per-GPU util/VRAM, node CPU/RAM, queue depth/running).
  // The store MUTATES in place (ring buffer); `seriesTick` bumps so selectors re-render.
  series: SeriesStore;
  seriesTick: number;
  lastEventTs: number | null;
}

// ST39 — series key builders (kept in one place so views + store agree).
export const seriesKeys = {
  gpuUtil: (clientId: string, index: number) => `gpu:${clientId}:${index}:util`,
  gpuVram: (clientId: string, index: number) => `gpu:${clientId}:${index}:vram`, // used %
  cpu: (clientId: string) => `cpu:${clientId}`,
  ram: (clientId: string) => `ram:${clientId}`, // used %
  queueDepth: (tool: string) => `queue:${tool}:depth`,
  queueRunning: (tool: string) => `queue:${tool}:running`,
};

// A stored /mcp session: the wire McpSession plus client-side close enrichment. `session.closed` MARKS the
// session closed (retained as history) rather than dropping it, so admins can inspect sessions that just
// ended instead of watching them flash out.
export type LiveSession = McpSession & { closed?: boolean; closed_ts?: number };

const CAP = 200;
// Max retained sessions (active are never pruned; oldest CLOSED are evicted past this).
const SESSIONS_CAP = 100;

function pruneClosedSessions(
  map: Record<string, LiveSession>,
  cap: number,
): Record<string, LiveSession> {
  const ids = Object.keys(map);
  if (ids.length <= cap) return map;
  const closedOldestFirst = Object.values(map)
    .filter((x) => x.closed)
    .sort((a, b) => (a.closed_ts ?? 0) - (b.closed_ts ?? 0));
  const remove = new Set(closedOldestFirst.slice(0, ids.length - cap).map((x) => x.session_id));
  if (remove.size === 0) return map;
  const out: Record<string, LiveSession> = {};
  for (const [k, v] of Object.entries(map)) if (!remove.has(k)) out[k] = v;
  return out;
}

function initialState(): DashboardState {
  return {
    connected: false,
    feed: [],
    calls: [],
    tasks: {},
    queues: {},
    streams: {},
    metrics: {},
    clients: {},
    publications: {},
    objects: {},
    routeInstances: {},
    sessions: {},
    series: new SeriesStore(),
    seriesTick: 0,
    lastEventTs: null,
  };
}

type Listener = () => void;

// Map the bus call `status` string onto the REST TaskState vocabulary the UI
// renders. Unknown values fall through as-is (StatusBadge tolerates any string).
function normalizeState(status: string): CallLog['state'] {
  return status.toUpperCase() as CallLog['state'];
}

class Store {
  private state: DashboardState = initialState();
  private listeners = new Set<Listener>();

  getState = (): DashboardState => this.state;

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  private set(next: DashboardState) {
    this.state = next;
    for (const fn of this.listeners) fn();
  }

  setConnected(connected: boolean) {
    if (this.state.connected === connected) return;
    this.set({ ...this.state, connected });
  }

  reset() {
    this.set(initialState());
  }

  /** Seed the live session map from a REST snapshot (api.mcpSessions()). Upserts
   * by session_id so any session.opened frames that already arrived survive. */
  seedSessions(rows: McpSession[]) {
    const sessions: Record<string, LiveSession> = { ...this.state.sessions };
    // REST snapshot lists only ACTIVE sessions; merge without clobbering a retained closed entry's flag.
    for (const r of rows) sessions[r.session_id] = { ...sessions[r.session_id], ...r, closed: false };
    this.set({ ...this.state, sessions });
  }

  // ST39 — record the number of RUNNING tasks for a tool into the series (queue "running" line).
  private recordRunning(tasks: Record<string, LiveTask>, tool: string, ts: number): void {
    let running = 0;
    for (const t of Object.values(tasks)) {
      if (t.canonical_tool === tool && (t.state === 'RUNNING' || t.state === 'DISPATCHED')) running += 1;
    }
    this.state.series.record(seriesKeys.queueRunning(tool), ts, running);
  }

  private pushFeed(s: DashboardState, id: string, ts: number, action: string, actor: string, target?: string): AuditEvent[] {
    const entry: AuditEvent = { id, ts: new Date(ts).toISOString(), action, actor, target };
    return [entry, ...s.feed].slice(0, CAP);
  }

  /** Coalesce a TASK's lifecycle into ONE feed row keyed by its task id: replace any existing row
   *  for the same task (updating action/target/ts to the latest state) and move it to the front,
   *  instead of prepending a fresh row per state transition. Without this, a single task spams the
   *  feed with task.queued → task.dispatched → task.succeeded rows — most visibly the assistant's
   *  keep-warm ping (a `text-llm chat` every few minutes while a chat is open), which read as a
   *  "queued ↔ dispatched" churn. The Tasks tab still shows each task's full per-phase timeline. */
  private pushTaskFeed(s: DashboardState, taskId: string, ts: number, action: string, target?: string): AuditEvent[] {
    const id = `task:${taskId}`;
    const entry: AuditEvent = { id, ts: new Date(ts).toISOString(), action, actor: 'system', target };
    return [entry, ...s.feed.filter((e) => e.id !== id)].slice(0, CAP);
  }

  /** Apply a validated event-bus frame. Pure projection, never throws. */
  applyFrame(frame: EventFrame) {
    const s = this.state;
    const ts = frame.ts;
    switch (frame.type) {
      case 'task.updated': {
        const d = frame.data;
        const prev = s.tasks[d.task_id];
        const tasks = {
          ...s.tasks,
          [d.task_id]: {
            id: d.task_id,
            canonical_tool: d.canonical_tool,
            network_id: d.network_id,
            state: d.state,
            priority: d.priority,
            fraction: d.fraction,
            provider_instance_id: d.provider_instance_id,
            // Preserve any terminal outcome already captured from task.completed.
            result: prev?.result,
            output_refs: prev?.output_refs,
            error: prev?.error,
            ts,
          },
        };
        this.recordRunning(tasks, d.canonical_tool, ts);
        this.set({
          ...s,
          tasks,
          feed: this.pushTaskFeed(s, d.task_id, ts, `task.${d.state.toLowerCase()}`, d.canonical_tool),
          seriesTick: s.seriesTick + 1,
          lastEventTs: ts,
        });
        return;
      }
      case 'task.completed': {
        const d = frame.data;
        const prev = s.tasks[d.task_id];
        const tool = d.canonical_tool ?? prev?.canonical_tool ?? '';
        const tasks = {
          ...s.tasks,
          [d.task_id]: {
            id: d.task_id,
            canonical_tool: tool,
            network_id: d.network_id ?? prev?.network_id,
            state: d.state,
            priority: prev?.priority ?? 0,
            fraction: prev?.fraction,
            provider_instance_id: prev?.provider_instance_id,
            result: d.result,
            output_refs: d.output_refs,
            error: d.error,
            ts,
          },
        };
        if (tool) this.recordRunning(tasks, tool, ts);
        this.set({
          ...s,
          tasks,
          feed: this.pushTaskFeed(s, d.task_id, ts, `task.${d.state.toLowerCase()}`, tool || d.task_id),
          seriesTick: s.seriesTick + 1,
          lastEventTs: ts,
        });
        return;
      }
      case 'call.logged': {
        const d = frame.data;
        const call: CallLog = {
          id: d.call_id,
          ts: new Date(ts).toISOString(),
          canonical_tool: d.canonical_tool,
          state: normalizeState(d.status),
          duration_ms: d.duration_ms,
          usage: { gpu_seconds: d.gpu_seconds ?? 0, bytes: 0, tokens: 0 },
        };
        this.set({ ...s, calls: [call, ...s.calls].slice(0, CAP), lastEventTs: ts });
        return;
      }
      case 'queue.changed': {
        const d = frame.data;
        s.series.record(seriesKeys.queueDepth(d.canonical_tool), ts, d.depth);
        this.set({
          ...s,
          queues: {
            ...s.queues,
            [d.canonical_tool]: {
              canonical_tool: d.canonical_tool,
              depth: d.depth,
              oldest_wait_ms: d.oldest_wait_ms,
              ts,
            },
          },
          seriesTick: s.seriesTick + 1,
          lastEventTs: ts,
        });
        return;
      }
      case 'stream.opened':
      case 'stream.closed': {
        const d = frame.data;
        const state = frame.type === 'stream.closed' ? 'CLOSED' : 'ACTIVE';
        this.set({
          ...s,
          streams: {
            ...s.streams,
            [d.stream_id]: {
              id: d.stream_id,
              canonical_tool: d.canonical_tool,
              channels: d.channels,
              consumer_count: d.consumer_count,
              state,
              ts,
            },
          },
          feed: this.pushFeed(s, frame.id, ts, frame.type, 'system', d.canonical_tool ?? d.stream_id),
          lastEventTs: ts,
        });
        return;
      }
      case 'publication.changed': {
        const d = frame.data;
        this.set({
          ...s,
          publications: {
            ...s.publications,
            [d.publication_id]: {
              id: d.publication_id,
              network_id: d.network_id,
              service_slug: d.service_slug,
              status: d.status,
              ts,
            },
          },
          feed: this.pushFeed(s, frame.id, ts, `publication.${d.status}`, 'system', d.service_slug ?? d.publication_id),
          lastEventTs: ts,
        });
        return;
      }
      case 'client.status': {
        const d = frame.data;
        this.set({
          ...s,
          clients: {
            ...s.clients,
            [d.client_id]: { id: d.client_id, status: d.status, node_profile: d.node_profile, ts },
          },
          feed: this.pushFeed(s, frame.id, ts, `client.${d.status}`, 'system', d.client_id),
          lastEventTs: ts,
        });
        return;
      }
      case 'client.metrics': {
        const d = frame.data;
        // ST39 — record the live sample into the time-series ring buffer.
        for (const g of d.gpus) {
          s.series.record(seriesKeys.gpuUtil(d.client_id, g.index), ts, g.util_pct);
          const usedPct = g.vram_total_mb > 0 ? ((g.vram_total_mb - g.vram_free_mb) / g.vram_total_mb) * 100 : 0;
          s.series.record(seriesKeys.gpuVram(d.client_id, g.index), ts, usedPct);
        }
        if (d.host) {
          s.series.record(seriesKeys.cpu(d.client_id), ts, d.host.cpu_pct);
          if (d.host.ram_total_mb > 0) {
            s.series.record(seriesKeys.ram(d.client_id), ts, (d.host.ram_used_mb / d.host.ram_total_mb) * 100);
          }
        }
        this.set({
          ...s,
          metrics: {
            ...s.metrics,
            [d.client_id]: {
              client_id: d.client_id,
              cpu_pct: d.host?.cpu_pct,
              ram_used_mb: d.host?.ram_used_mb,
              ram_total_mb: d.host?.ram_total_mb,
              store: d.store,
              // Project the bus GPU shape into the UI's GpuStat. name + temp_c are now carried on the
              // bus (older gateways default them to ''/0 via the Zod schema).
              gpus: d.gpus.map((g) => ({
                index: g.index,
                name: g.name,
                vram_total_mb: g.vram_total_mb,
                vram_free_mb: g.vram_free_mb,
                util_pct: g.util_pct,
                temp_c: g.temp_c,
              })),
              ts,
            },
          },
          seriesTick: s.seriesTick + 1,
          lastEventTs: ts,
        });
        return;
      }
      case 'object.changed': {
        const d = frame.data;
        this.set({
          ...s,
          objects: {
            ...s.objects,
            [d.s3_key]: {
              s3_key: d.s3_key,
              network_id: d.network_id,
              op: d.op,
              instance: d.instance,
              size: d.size,
              is_global: d.is_global,
              ts,
            },
          },
          feed: this.pushFeed(s, frame.id, ts, `object.${d.op.toLowerCase()}`, 'system', d.s3_key),
          lastEventTs: ts,
        });
        return;
      }
      case 'route.instantiated':
      case 'route.completed':
      case 'route.errored': {
        const d = frame.data;
        const status =
          frame.type === 'route.completed'
            ? 'completed'
            : frame.type === 'route.errored'
              ? 'errored'
              : 'active';
        // Keep the hop chain from the instantiated frame (completed/errored omit it).
        const prev = s.routeInstances[d.instance_id];
        this.set({
          ...s,
          routeInstances: {
            ...s.routeInstances,
            [d.instance_id]: {
              instance_id: d.instance_id,
              route_id: d.route_id,
              route_name: d.route_name,
              network_id: d.network_id,
              version: d.version,
              status,
              hops: d.hops.length ? d.hops : (prev?.hops ?? []),
              error: d.error,
              ts,
            },
          },
          feed: this.pushFeed(
            s,
            frame.id,
            ts,
            frame.type,
            'system',
            `${d.route_name} v${d.version}`,
          ),
          lastEventTs: ts,
        });
        return;
      }
      case 'session.opened': {
        const d = frame.data;
        // Upsert active (clears any prior closed flag if a session_id is reused).
        this.set({
          ...s,
          sessions: { ...s.sessions, [d.session_id]: { ...d, closed: false } },
          feed: this.pushFeed(s, frame.id, ts, 'session.opened', d.principal, d.session_id),
          lastEventTs: ts,
        });
        return;
      }
      case 'session.closed': {
        const d = frame.data;
        const existing = s.sessions[d.session_id];
        const feed = this.pushFeed(s, frame.id, ts, 'session.closed', 'system', d.session_id);
        if (!existing) {
          // Never saw it open (missed frame) — nothing to retain; just log it.
          this.set({ ...s, feed, lastEventTs: ts });
          return;
        }
        // Retain as history: mark closed, keep it visible, prune oldest closed past the cap.
        const sessions = pruneClosedSessions(
          { ...s.sessions, [d.session_id]: { ...existing, closed: true, closed_ts: ts } },
          SESSIONS_CAP,
        );
        this.set({ ...s, sessions, feed, lastEventTs: ts });
        return;
      }
    }
  }
}

export const store = new Store();

/** Selector for the /mcp session map — active + recently-closed (H64/H67). */
export const selectSessions = (s: DashboardState): Record<string, LiveSession> => s.sessions;

// ST39 — subscribe to the time-series (via seriesTick) and read points for a set of keys.
// `agg`/`sinceMs` forwarded to SeriesStore.points. Re-computed whenever a sample lands.
export function useSeries(
  keys: string[],
  agg: 'last' | 'avg' | 'min' | 'max' = 'last',
  sinceMs?: number,
): { key: string; points: { ts: number; value: number }[] }[] {
  // seriesTick drives the re-render; the actual read is imperative off the store's ring buffer.
  useStore((s) => s.seriesTick);
  const s = store.getState();
  return keys.map((key) => ({ key, points: s.series.points(key, agg, sinceMs) }));
}

// React binding. `selector` must return a stable value for unchanged state.
export function useStore<T>(selector: (s: DashboardState) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getState()),
    () => selector(store.getState()),
  );
}
